import type { ReactNode } from 'react';
import { UMButton, UMIcon } from '../../urbanmap/_components';
import { PlanLogo, Pill } from '../proposition-c/ui';
import { fmt } from './domain';
import { PC, UMR, caption } from './tokens';
import type { PlanProject } from './types';

interface HeaderProps {
  project: PlanProject;
  umChanges: number;
  onSync: () => void;
  onSettings: () => void;
  settingsOpen: boolean;
  syncOpen: boolean;
}

/** En-tête projet : nom, provenance UM, bandeau « UrbanMap a évolué » + Synchroniser, Paramètres. */
export function ProjectHeader({ project, umChanges, onSync, onSettings, settingsOpen, syncOpen }: HeaderProps) {
  return (
    <header
      style={{
        height: PC.headerHeight, minHeight: PC.headerHeight, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 16, padding: '0 16px 0 20px',
        borderTop: `3px solid ${PC.accent}`, borderBottom: `1px solid ${UMR.border}`, background: UMR.white, position: 'relative', zIndex: 40,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, lineHeight: '20px', color: UMR.textStrong, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.name}</span>
        <span style={{ ...caption, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          <UMIcon name="content_copy" size={13} color={PC.accent} />
          Copie UrbanMap du {project.snapshotDate} · {project.perimeterLabel} · {fmt(project.buildingCount)} bâtiments
        </span>
      </div>
      <span style={{ flex: 1 }} />
      {umChanges > 0 && (
        <Pill tone="warning" icon="update" onClick={onSync} title="Voir les écarts avec UrbanMap">
          UrbanMap a évolué · {fmt(umChanges)} bâtiment{umChanges > 1 ? 's' : ''}
        </Pill>
      )}
      <Toggle on={syncOpen}><UMButton variant="secondary" icon="sync" onClick={onSync}>Synchroniser</UMButton></Toggle>
      <Toggle on={settingsOpen}><UMButton variant="secondary" icon="settings" onClick={onSettings}>Paramètres</UMButton></Toggle>
    </header>
  );
}

function Toggle({ on, children }: { on: boolean; children: ReactNode }) {
  return <span style={{ display: 'inline-flex', boxShadow: on ? `inset 0 -2px 0 ${PC.accent}` : undefined }}>{children}</span>;
}

const RAIL_ITEMS = [
  { id: 'projects', icon: 'folder_open', label: 'Projets' },
  { id: 'map', icon: 'map', label: 'Carte' },
] as const;

/** Rail Plan (mêmes métriques que UMRail : 80 px, items 59 px). */
export function ProjectRail({ onProjects }: { onProjects: () => void }) {
  return (
    <nav aria-label="Navigation Plan" style={{ width: UMR.size.railWidth, minWidth: UMR.size.railWidth, height: '100%', borderRight: `1px solid ${UMR.border}`, background: UMR.white, display: 'flex', flexDirection: 'column', zIndex: 30 }}>
      <div style={{ padding: '16px 0', display: 'flex', justifyContent: 'center' }}>
        <PlanLogo size={UMR.size.logo + 8} />
      </div>
      {RAIL_ITEMS.map((item) => {
        const active = item.id === 'map';
        return (
          <button
            key={item.id}
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={active ? undefined : onProjects}
            style={{
              width: '100%', height: UMR.size.railItemHeight, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 0', border: 'none',
              borderLeft: `3px solid ${active ? PC.accent : 'transparent'}`, background: active ? PC.accentBg : 'transparent', color: active ? PC.accentStrong : UMR.text,
              cursor: active ? 'default' : 'pointer', fontFamily: UMR.font,
            }}
          >
            <UMIcon name={item.icon} size={UMR.size.railIcon} color={active ? PC.accentStrong : UMR.text} />
            <span style={{ fontSize: UMR.size.railLabel, lineHeight: '16px' }}>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
